/**
 * Thin client for the Hub's GitHub App endpoints. Mirrors the server
 * surface in `server/routes/github-app.ts` and `deploy-from-git.ts`.
 */

export interface GitHubStatus {
  configured: boolean;
  installUrl: string;
  patMode: boolean;
}

export interface GitHubInstallation {
  installation_id: number;
  account_login: string;
}

export interface GitHubRepo {
  owner: string;
  name: string;
  fullName: string;
  defaultBranch: string;
}

export interface GitHubBranch {
  name: string;
  sha: string;
}

export interface DeployFromGitInput {
  name: string;
  description?: string;
  team?: string;
  category?: string;
  installationId: number;
  owner: string;
  repo: string;
  branch: string;
  autoRedeploy?: boolean;
}

/** Fetch App-configured flag + install URL. */
export async function fetchGitHubStatus(token: string): Promise<GitHubStatus | null> {
  return fetchJson<GitHubStatus>(token, '/api/github/status');
}

/** Fetch installations linked to the caller. */
export async function fetchGitHubInstallations(token: string): Promise<GitHubInstallation[]> {
  return (await fetchJson<GitHubInstallation[]>(token, '/api/github/installations')) ?? [];
}

/** Fetch repos visible to a specific installation. */
export async function fetchGitHubRepos(token: string, installationId: number): Promise<GitHubRepo[]> {
  return (await fetchJson<GitHubRepo[]>(token, `/api/github/installations/${installationId}/repos`)) ?? [];
}

/** Fetch branches for a repo inside an installation. */
export async function fetchGitHubBranches(
  token: string,
  installationId: number,
  owner: string,
  repo: string,
): Promise<GitHubBranch[]> {
  return (await fetchJson<GitHubBranch[]>(
    token,
    `/api/github/installations/${installationId}/repos/${owner}/${repo}/branches`,
  )) ?? [];
}

/** Kick off a deploy using an existing GitHub connection. */
export async function postDeployFromGit(
  token: string,
  input: DeployFromGitInput,
): Promise<{ ok: boolean; data: { slug?: string; appUrl?: string; error?: string } }> {
  const response = await fetch('/api/apps/deploy-from-git', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = (await response.json()) as { slug?: string; appUrl?: string; error?: string };
  return { ok: response.ok, data };
}

/** Shared GET JSON helper — returns null on network/parse/response errors. */
async function fetchJson<T>(token: string, path: string): Promise<T | null> {
  try {
    const response = await fetch(path, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}
